import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { apiFetch } from '../services/api';
import { tt } from '../utils/consoleText';
import { useEntitiesStore } from './entities';
import type { Device } from '@/types';

export type LightsViewMode = 'rooms' | 'types' | 'groups';
export type LightTypeKey = 'onoff' | 'dimmable' | 'tunable' | 'color';
export type LightGroupCapability = 'all' | 'brightness' | 'colorTemperature' | 'color';
export type LightScopeKind = 'all' | 'room' | 'type' | 'group';

export interface LightsManagedDevice {
  id: string;
  deviceId: string;
  enabled: boolean;
  sortOrder: number;
}

export interface LightGroup {
  id: string;
  name: string;
  icon: string;
  capability: LightGroupCapability;
  enabled: boolean;
  sortOrder: number;
  deviceIds: string[];
}

export interface LightCommandPayload {
  power?: boolean | null;
  brightness?: number | null;
  color?: string | null;
  colorTemperature?: number | null;
}

export interface LightPreset {
  id: string;
  name: string;
  icon: string;
  scopeKind: LightScopeKind;
  scopeId: string | null;
  command: LightCommandPayload;
  sortOrder: number;
}

export interface LightModeDeviceConfig {
  enabled?: boolean;
  power?: boolean;
  brightness?: number;
  color?: string;
  colorTemperature?: number;
}

export interface LightModePreset {
  id: string;
  name: string;
  icon: string;
  sortOrder: number;
  config: Record<string, LightModeDeviceConfig>;
  automations: Record<string, { enabled: boolean }>;
}

export interface LightCommandScope {
  kind: LightScopeKind;
  id?: string | null;
}

interface LightsSettingsPayload {
  hiddenDeviceIds: string[];
  devices: LightsManagedDevice[];
  groups: LightGroup[];
  presets: LightPreset[];
  modes: LightModePreset[];
  activeModeId: string | null;
  lastView: LightsViewMode;
}

interface LightEntry {
  managed: LightsManagedDevice;
  device: Device;
  lightType: LightTypeKey;
}

export const LIGHT_TYPE_ORDER: LightTypeKey[] = ['onoff', 'dimmable', 'tunable', 'color'];

const LIGHT_TYPE_ICONS: Record<LightTypeKey, string> = {
  onoff: 'lightbulb_outline',
  dimmable: 'brightness_medium',
  tunable: 'wb_iridescent',
  color: 'palette',
};

/**
 * A light type and a group capability as an English/Slovene pair — the shape
 * the app's inline copy uses (`consoleText.ts`, `config/serviceConsole.ts`).
 * The tables are written out as pairs and the getters below pick their half, so
 * the several surfaces that render a label (the summary widget's pills, the
 * lights page's headings, the settings lists and the group editor's capability
 * options) all follow a language switch through one table.
 */
const LIGHT_TYPE_LABELS: Record<LightTypeKey, [string, string]> = {
  onoff: ['On / Off', 'Vklop / Izklop'],
  dimmable: ['Dimmable', 'Zatemnitev'],
  tunable: ['Tunable White', 'Nastavljiva bela'],
  color: ['Color', 'Barva'],
};

const LIGHT_GROUP_CAPABILITY_LABELS: Record<LightGroupCapability, [string, string]> = {
  all: ['Any light', 'Vsaka luč'],
  brightness: ['Dimmable', 'Zatemnitev'],
  colorTemperature: ['Tunable white', 'Nastavljiva bela'],
  color: ['Color', 'Barva'],
};

function normalizeKey(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/** A device value's key and name, as the light matchers read them. */
interface DeviceValueLike {
  valueKey: string;
  name?: string | null;
  currentValue?: unknown;
}

function isDeviceValueLike(row: unknown): row is DeviceValueLike {
  return typeof row === 'object' && row !== null && 'valueKey' in row && typeof row.valueKey === 'string';
}

function getDeviceValues(device: Device): DeviceValueLike[] {
  if (Array.isArray(device.deviceValues)) return device.deviceValues;
  // Older hub payloads carried the same rows under the legacy `values` alias.
  const legacy = 'values' in device ? device.values : undefined;
  return Array.isArray(legacy) ? legacy.filter(isDeviceValueLike) : [];
}

function hasValueKey(device: Device, keys: string[]): boolean {
  const normalizedKeys = new Set(keys.map(normalizeKey));
  return getDeviceValues(device).some((value) => {
    const valueKey = normalizeKey(value.valueKey);
    const valueName = normalizeKey(value.name ?? '');
    return normalizedKeys.has(valueKey) || normalizedKeys.has(valueName);
  });
}

function buildId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function compareDevices(a: Device, b: Device): number {
  return (
    (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
    a.deviceName.localeCompare(b.deviceName, undefined, { sensitivity: 'base' })
  );
}

export function isLightDevice(device: Device): boolean {
  const control = normalizeKey(device.deviceControl ?? device.controlType);
  const type = normalizeKey(device.deviceType);
  return control === 'light' || control.startsWith('light_') || control.includes('light') || type.includes('light');
}

export function getLightCapabilities(device: Device): {
  power: boolean;
  brightness: boolean;
  colorTemperature: boolean;
  color: boolean;
} {
  return {
    power: hasValueKey(device, ['power', 'on_off', 'state']),
    brightness: hasValueKey(device, ['brightness', 'dimming', 'level']),
    colorTemperature: hasValueKey(device, ['color_temp', 'color_temperature']),
    color: hasValueKey(device, ['color', 'rgb']),
  };
}

export function supportsGroupCapability(device: Device, capability: LightGroupCapability): boolean {
  const features = getLightCapabilities(device);
  switch (capability) {
    case 'brightness':
      return features.brightness;
    case 'colorTemperature':
      return features.colorTemperature;
    case 'color':
      return features.color;
    default:
      return true;
  }
}

export function detectLightType(device: Device): LightTypeKey {
  const features = getLightCapabilities(device);
  if (features.color) return 'color';
  if (features.colorTemperature) return 'tunable';
  if (features.brightness) return 'dimmable';
  return 'onoff';
}

export function isLightOn(device: Device): boolean {
  const powerValue = getDeviceValues(device).find((value) => {
    const key = normalizeKey(value.valueKey);
    return key === 'power' || key === 'on_off' || key === 'state';
  });
  const currentValue = powerValue?.currentValue;
  return currentValue === true || currentValue === 1 || currentValue === 'on' || currentValue === 'true';
}

export function getLightTypeIcon(type: LightTypeKey): string {
  return LIGHT_TYPE_ICONS[type] ?? 'lightbulb';
}

export function getLightTypeLabel(type: LightTypeKey): string {
  const pair = LIGHT_TYPE_LABELS[type];
  return pair ? tt(pair[0], pair[1]) : tt('Lights', 'Luči');
}

export function getLightGroupCapabilityLabel(capability: LightGroupCapability): string {
  const pair = LIGHT_GROUP_CAPABILITY_LABELS[capability];
  return pair ? tt(pair[0], pair[1]) : tt('Any light', 'Vsaka luč');
}

/** An automation row of a light mode (only `enabled` is read here). */
interface RawLightAutomation {
  enabled?: unknown;
}

/**
 * One row of the hub's lights-settings payload. Only the fields the sanitizers
 * below read are named — the rest of the row stays as the hub sent it.
 */
interface RawLightSettingsRow {
  id?: string;
  deviceId?: string;
  name?: string;
  icon?: string;
  enabled?: unknown;
  sortOrder?: unknown;
  capability?: string;
  scopeKind?: string;
  scopeId?: unknown;
  deviceIds?: unknown;
  command?: { power?: unknown; brightness?: unknown; color?: unknown; colorTemperature?: unknown };
  config?: Record<string, LightModeDeviceConfig>;
  automations?: Record<string, RawLightAutomation>;
}

/** The capability / scope sets a light row may carry. */
const GROUP_CAPABILITIES: readonly LightGroupCapability[] = ['all', 'brightness', 'colorTemperature', 'color'];
const PRESET_SCOPE_KINDS: readonly LightScopeKind[] = ['all', 'room', 'type', 'group'];
const LIGHT_VIEW_KEYS = ['rooms', 'types', 'groups'] as const;

/** The row's value when it is one of `allowed`, else undefined. */
function readOneOf<T extends string>(value: string | undefined, allowed: readonly T[]): T | undefined {
  return allowed.find((option) => option === value);
}

/** The row's sort order when it is a finite number, else undefined. */
function readSortOrder(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** What `/lights/command` answers: how the command landed per device. */
interface LightCommandResult {
  succeededDevices: number;
  targetedDevices: number;
  skippedDevices: number;
  [key: string]: unknown;
}

function sanitizeManagedDevices(input: unknown): LightsManagedDevice[] {
  return Array.isArray(input)
    ? input
        .map((item: RawLightSettingsRow, index) => ({
          id: item.id ?? item.deviceId ?? buildId('light-device'),
          deviceId: item.deviceId ?? item.id ?? '',
          enabled: item.enabled !== false,
          sortOrder: readSortOrder(item.sortOrder) ?? index,
        }))
        .filter((item) => item.deviceId.length > 0)
    : [];
}

function sanitizeGroups(input: unknown): LightGroup[] {
  return Array.isArray(input)
    ? input.map((item: RawLightSettingsRow, index) => ({
        id: item.id ?? buildId('light-group'),
        name: item.name ?? tt(`Light Group ${index + 1}`, `Skupina luči ${index + 1}`),
        icon: item.icon ?? 'lightbulb',
        capability: readOneOf(item.capability, GROUP_CAPABILITIES) ?? 'all',
        enabled: item.enabled !== false,
        sortOrder: readSortOrder(item.sortOrder) ?? index,
        deviceIds: Array.isArray(item.deviceIds)
          ? item.deviceIds.filter((value: unknown): value is string => typeof value === 'string')
          : [],
      }))
    : [];
}

function sanitizePresets(input: unknown): LightPreset[] {
  return Array.isArray(input)
    ? input.map((item: RawLightSettingsRow, index) => {
        const command = item.command ?? {};
        return {
          id: item.id ?? buildId('light-preset'),
          name: item.name ?? tt(`Preset ${index + 1}`, `Prednastavitev ${index + 1}`),
          icon: item.icon ?? 'lightbulb',
          scopeKind: readOneOf(item.scopeKind, PRESET_SCOPE_KINDS) ?? 'all',
          scopeId: typeof item.scopeId === 'string' ? item.scopeId : null,
          command: {
            power: typeof command.power === 'boolean' ? command.power : null,
            brightness: typeof command.brightness === 'number' ? command.brightness : null,
            color: typeof command.color === 'string' ? command.color : null,
            colorTemperature: typeof command.colorTemperature === 'number' ? command.colorTemperature : null,
          },
          sortOrder: readSortOrder(item.sortOrder) ?? index,
        };
      })
    : [];
}

function sanitizeModes(input: unknown): LightModePreset[] {
  return Array.isArray(input)
    ? input.map((item: RawLightSettingsRow, index) => ({
        id: item.id ?? buildId('light-mode'),
        name: item.name ?? tt(`Mode ${index + 1}`, `Način ${index + 1}`),
        icon: item.icon ?? 'light_mode',
        sortOrder: readSortOrder(item.sortOrder) ?? index,
        config:
          item.config && typeof item.config === 'object'
            ? Object.fromEntries(
                Object.entries(item.config).map(([deviceId, cfg]) => [
                  deviceId,
                  {
                    enabled: typeof cfg.enabled === 'boolean' ? cfg.enabled : true,
                    power: typeof cfg.power === 'boolean' ? cfg.power : undefined,
                    brightness: typeof cfg.brightness === 'number' ? cfg.brightness : undefined,
                    color: typeof cfg.color === 'string' ? cfg.color : undefined,
                    colorTemperature: typeof cfg.colorTemperature === 'number' ? cfg.colorTemperature : undefined,
                  },
                ]),
              )
            : {},
        automations:
          item.automations && typeof item.automations === 'object'
            ? Object.fromEntries(
                Object.entries(item.automations).map(([id, t]) => [id, { enabled: t.enabled !== false }]),
              )
            : {},
      }))
    : [];
}

export const useLightsStore = defineStore('lights', () => {
  const entitiesStore = useEntitiesStore();

  const hiddenDeviceIds = ref<string[]>([]);
  const managedDevices = ref<LightsManagedDevice[]>([]);
  const groups = ref<LightGroup[]>([]);
  const presets = ref<LightPreset[]>([]);
  const modes = ref<LightModePreset[]>([]);
  const activeModeId = ref<string | null>(null);
  const lastView = ref<LightsViewMode>('rooms');
  const settingsLoading = ref(false);
  const settingsSaving = ref(false);
  const error = ref<string | null>(null);

  const activeMode = computed(() => modes.value.find((m) => m.id === activeModeId.value) ?? null);

  const autoDetectedLightDevices = computed<Device[]>(() =>
    entitiesStore.devices.filter(isLightDevice).sort(compareDevices),
  );

  function buildSeedDevices(): LightsManagedDevice[] {
    return autoDetectedLightDevices.value.map((device, index) => ({
      id: device.id,
      deviceId: device.id,
      enabled: !hiddenDeviceIds.value.includes(device.id),
      sortOrder: device.sortOrder ?? index,
    }));
  }

  const resolvedManagedDevices = computed<LightsManagedDevice[]>(() => {
    if (managedDevices.value.length > 0) return managedDevices.value;
    return buildSeedDevices();
  });

  const compatibleDevices = computed<Device[]>(() => {
    const existingIds = new Set(resolvedManagedDevices.value.map((item) => item.deviceId));
    return autoDetectedLightDevices.value.filter((device) => !existingIds.has(device.id));
  });

  const lightEntries = computed<LightEntry[]>(() =>
    resolvedManagedDevices.value
      .map((managed) => {
        const device = entitiesStore.getDevice(managed.deviceId);
        return device ? { managed, device, lightType: detectLightType(device) } : null;
      })
      .filter((entry): entry is LightEntry => Boolean(entry))
      .sort((a, b) => (a.managed.sortOrder ?? 0) - (b.managed.sortOrder ?? 0) || compareDevices(a.device, b.device)),
  );

  const enabledLightEntries = computed<LightEntry[]>(() => lightEntries.value.filter((entry) => entry.managed.enabled));

  const lightDevices = computed<Device[]>(() => enabledLightEntries.value.map((entry) => entry.device));

  const devicesByLightType = computed<Map<LightTypeKey, Device[]>>(() => {
    const map = new Map<LightTypeKey, Device[]>();
    for (const type of LIGHT_TYPE_ORDER) map.set(type, []);
    for (const entry of enabledLightEntries.value) {
      map.get(entry.lightType)?.push(entry.device);
    }
    return map;
  });

  const groupEntries = computed<Array<{ group: LightGroup; devices: Device[] }>>(() =>
    [...groups.value]
      .filter((group) => group.enabled)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((group) => ({
        group,
        devices: enabledLightEntries.value
          .filter(
            (entry) =>
              group.deviceIds.includes(entry.device.id) && supportsGroupCapability(entry.device, group.capability),
          )
          .map((entry) => entry.device)
          .sort(compareDevices),
      })),
  );

  function applySettingsPayload(data: Partial<LightsSettingsPayload>): void {
    hiddenDeviceIds.value = Array.isArray(data?.hiddenDeviceIds)
      ? data.hiddenDeviceIds.filter((value: unknown): value is string => typeof value === 'string')
      : [];
    managedDevices.value = sanitizeManagedDevices(data?.devices);
    groups.value = sanitizeGroups(data?.groups);
    presets.value = sanitizePresets(data?.presets);
    modes.value = sanitizeModes(data?.modes);
    activeModeId.value = typeof data?.activeModeId === 'string' ? data.activeModeId : null;
    lastView.value =
      readOneOf(typeof data?.lastView === 'string' ? data.lastView : undefined, LIGHT_VIEW_KEYS) ?? 'rooms';
  }

  function buildSettingsPayload(): LightsSettingsPayload {
    return {
      hiddenDeviceIds: hiddenDeviceIds.value,
      devices: managedDevices.value,
      groups: groups.value,
      presets: presets.value,
      modes: modes.value,
      activeModeId: activeModeId.value,
      lastView: lastView.value,
    };
  }

  async function fetchSettings(): Promise<void> {
    settingsLoading.value = true;
    error.value = null;
    try {
      const data = await apiFetch('/lights/settings');
      applySettingsPayload(data);
    } catch (__caught: unknown) {
      const err = __caught as Error;
      error.value = err.message ?? tt('Failed to load lights settings', 'Nastavitev luči ni bilo mogoče naložiti');
      console.error('[lightsStore] fetchSettings error', err);
    } finally {
      settingsLoading.value = false;
    }
  }

  async function saveSettings(): Promise<void> {
    settingsSaving.value = true;
    try {
      const data = await apiFetch('/lights/settings', {
        method: 'PATCH',
        body: JSON.stringify(buildSettingsPayload()),
      });
      applySettingsPayload(data);
    } finally {
      settingsSaving.value = false;
    }
  }

  async function ensureManagedDevices(): Promise<void> {
    if (managedDevices.value.length > 0 || autoDetectedLightDevices.value.length === 0) return;
    managedDevices.value = buildSeedDevices();
    await saveSettings();
  }

  /**
   * Take the lights the hub reports that the saved settings do not carry into the
   * managed list — the settings page's "Detect Lights" action.
   *
   * `ensureManagedDevices` only ever seeds a hub that has none at all, so a light
   * the hub reports after that first visit had no way in: the seed saw a non-empty
   * list and returned, and the button looked dead. This is that re-scan. It returns
   * how many were added, so the caller can say what happened instead of leaving the
   * click unanswered.
   */
  async function detectLightDevices(): Promise<number> {
    const missing = compatibleDevices.value;
    if (missing.length === 0) return 0;

    // The resolved list, not the raw one: a hub that has never been saved yet is
    // read through the seed, and appending to the seed keeps `id`/`deviceId` paired.
    const base = resolvedManagedDevices.value;
    const additions: LightsManagedDevice[] = missing.map((device, index) => ({
      id: device.id,
      deviceId: device.id,
      enabled: !hiddenDeviceIds.value.includes(device.id),
      sortOrder: base.length + index,
    }));

    managedDevices.value = [...base, ...additions];
    await saveSettings();
    return additions.length;
  }

  async function setLastView(view: LightsViewMode): Promise<void> {
    if (lastView.value === view) return;
    lastView.value = view;
    await saveSettings();
  }

  async function updateManagedDevice(id: string, changes: Partial<LightsManagedDevice>): Promise<void> {
    await ensureManagedDevices();
    managedDevices.value = managedDevices.value.map((item) => (item.id === id ? { ...item, ...changes } : item));
    await saveSettings();
  }

  async function createGroup(data: Omit<LightGroup, 'id' | 'sortOrder'> & { sortOrder?: number }): Promise<LightGroup> {
    const group: LightGroup = {
      id: buildId('light-group'),
      sortOrder: data.sortOrder ?? groups.value.length,
      ...data,
    };
    groups.value = [...groups.value, group].sort((a, b) => a.sortOrder - b.sortOrder);
    await saveSettings();
    return group;
  }

  async function updateGroup(groupId: string, changes: Partial<LightGroup>): Promise<void> {
    groups.value = groups.value.map((group) => (group.id === groupId ? { ...group, ...changes } : group));
    await saveSettings();
  }

  async function deleteGroup(groupId: string): Promise<void> {
    groups.value = groups.value.filter((group) => group.id !== groupId);
    presets.value = presets.value.filter((preset) => !(preset.scopeKind === 'group' && preset.scopeId === groupId));
    await saveSettings();
  }

  async function createPreset(
    data: Omit<LightPreset, 'id' | 'sortOrder'> & { sortOrder?: number },
  ): Promise<LightPreset> {
    const preset: LightPreset = {
      id: buildId('light-preset'),
      sortOrder: data.sortOrder ?? presets.value.length,
      ...data,
    };
    presets.value = [...presets.value, preset].sort((a, b) => a.sortOrder - b.sortOrder);
    await saveSettings();
    return preset;
  }

  async function updatePreset(presetId: string, changes: Partial<LightPreset>): Promise<void> {
    presets.value = presets.value.map((preset) => (preset.id === presetId ? { ...preset, ...changes } : preset));
    await saveSettings();
  }

  async function deletePreset(presetId: string): Promise<void> {
    presets.value = presets.value.filter((preset) => preset.id !== presetId);
    await saveSettings();
  }

  async function applyCommand(scope: LightCommandScope, command: LightCommandPayload): Promise<LightCommandResult> {
    return apiFetch('/lights/command', {
      method: 'POST',
      body: JSON.stringify({ scope, command }),
    });
  }

  async function applyPreset(preset: LightPreset): Promise<LightCommandResult> {
    return applyCommand({ kind: preset.scopeKind, id: preset.scopeId }, preset.command);
  }

  // ── Light modes ─────────────────────────────────────────────────────────────

  async function fetchModes(): Promise<void> {
    try {
      const data = await apiFetch('/lights/modes');
      modes.value = sanitizeModes(data?.modes);
      activeModeId.value = typeof data?.activeModeId === 'string' ? data.activeModeId : null;
    } catch (err) {
      console.error('[lightsStore] fetchModes error', err);
    }
  }

  async function createMode(data: {
    name: string;
    icon?: string;
    config?: Record<string, LightModeDeviceConfig>;
    automations?: Record<string, { enabled: boolean }>;
  }): Promise<LightModePreset> {
    const mode = await apiFetch('/lights/modes', { method: 'POST', body: JSON.stringify(data) });
    modes.value = sanitizeModes([...modes.value, mode]);
    return mode;
  }

  async function updateMode(modeId: string, data: Partial<LightModePreset>): Promise<LightModePreset> {
    const updated = await apiFetch(`/lights/modes/${modeId}`, { method: 'PUT', body: JSON.stringify(data) });
    modes.value = modes.value.map((m) => (m.id === modeId ? updated : m));
    return updated;
  }

  async function deleteMode(modeId: string): Promise<void> {
    await apiFetch(`/lights/modes/${modeId}`, { method: 'DELETE' });
    modes.value = modes.value.filter((m) => m.id !== modeId);
    if (activeModeId.value === modeId) activeModeId.value = null;
  }

  async function activateMode(modeId: string): Promise<void> {
    await apiFetch(`/lights/modes/${modeId}/activate`, { method: 'POST' });
    activeModeId.value = modeId;
  }

  async function clearManualMode(): Promise<void> {
    await apiFetch('/lights/modes/manual', { method: 'POST' });
    activeModeId.value = null;
  }

  async function loadAll(): Promise<void> {
    await fetchSettings();
    await fetchModes();
  }

  return {
    hiddenDeviceIds,
    managedDevices,
    groups,
    presets,
    modes,
    activeModeId,
    activeMode,
    lastView,
    settingsLoading,
    settingsSaving,
    error,
    autoDetectedLightDevices,
    resolvedManagedDevices,
    compatibleDevices,
    lightEntries,
    enabledLightEntries,
    lightDevices,
    devicesByLightType,
    groupEntries,
    fetchSettings,
    saveSettings,
    ensureManagedDevices,
    detectLightDevices,
    setLastView,
    updateManagedDevice,
    createGroup,
    updateGroup,
    deleteGroup,
    createPreset,
    updatePreset,
    deletePreset,
    applyCommand,
    applyPreset,
    fetchModes,
    createMode,
    updateMode,
    deleteMode,
    activateMode,
    clearManualMode,
    loadAll,
  };
});
